import { useMemo } from "react";
import { useSearchParams } from "react-router";
import type {
  FilterTree,
  ViewColumnSpec,
  ViewSort,
} from "@colab/shared";
import type { SavedView } from "./types";

// Resolves the currently-active view from the `?view=<id>` URL param.
// Returns `null` when no view is active (the chip-toolbar fallback should
// drive state in that case). Callers can derive `filter / columns / sort`
// from the returned row for use in their list query hook.
//
// Why a separate helper: every consumer page needs the same resolve →
// derive flow. Centralising it ensures the URL param key stays
// consistent (`?view=`) and prevents drift across pages.

export function useActiveView(
  views: SavedView[] | undefined,
): {
  view: SavedView | null;
  filter: FilterTree | null;
  columns: ViewColumnSpec[];
  sort: ViewSort | null;
} {
  const [searchParams] = useSearchParams();
  const viewId = searchParams.get("view");

  return useMemo(() => {
    if (!viewId || !views || views.length === 0) {
      return { view: null, filter: null, columns: [], sort: null };
    }
    const view = views.find((v) => v.id === viewId) ?? null;
    if (!view) {
      return { view: null, filter: null, columns: [], sort: null };
    }
    return {
      view,
      filter: view.filter,
      columns: view.columns,
      sort: view.sort,
    };
  }, [views, viewId]);
}

// Small URL helper — used by SavedViewTabs to construct the next URL when
// the user picks a tab. Kept inside this module so the convention (drop
// the `?filter=` ad-hoc param when switching views) stays in one place.
export function buildViewParamUpdate(
  current: URLSearchParams,
  viewId: string | null,
): URLSearchParams {
  const next = new URLSearchParams(current);
  if (viewId) {
    next.set("view", viewId);
  } else {
    next.delete("view");
  }
  return next;
}

export function applyEntityFilterToList<TList>(
  list: TList[] | undefined,
): TList[] {
  // Placeholder for any cross-entity post-filter logic the SavedViewTabs
  // consumer might want to share. Today every page applies its own filter
  // in its useXxx hook; this stub keeps the API stable for future hoisting.
  return list ?? [];
}
